import classNames from "classnames";
import { SCROLL_OFFSET_TOP, UPLOAD_MIN_DELAY, STATES } from "../constants";
import EformApiClient from "../api/EformsApi";
import Number from "../interfaces/Number";
import ResponsiveClasses from "../interfaces/ResponsiveClasses";
import { FormStateValue, FormStateValues } from "../interfaces/Form/State";
import { File as FileI, Files } from "../interfaces/File";
import Uploads from "../interfaces/Uploads";
import Option from "../interfaces/Option";

export const getWidgetPath = (name: string, isCustom = false) => {
  if (!isCustom) return name;
  return `${name.split(":").join("/")}`;
};

export const updateObjectByPath = (object: any, path: string, value: any) => {
  const out = { ...object };
  const parts = path.split("/"); //every path starts with /, so part[0] is an empty string, that's why it's not used
  if (parts.length === 2) {
    out[parts[1]] = value;
    return out;
  }
  out[parts[1]] = updateObjectByPath(
    out[parts[1]],
    path.slice(parts[1].length + 1, path.length),
    value
  );
  return out;
};

export const sanitizeNumber = (val: string, type?: Number) => {
  if (!val) return val; //handles empty input
  val = val.replace(",", ".");
  let parsingFunc = type === Number.int ? parseInt : parseFloat;
  const number = parsingFunc(val);
  return isNaN(number) || number === Infinity ? undefined : number;
};

export const scopeToID = (scope: string) => {
  return scope
    .split("/")
    .filter((part: string) => part !== "properties")
    .join("/");
};

export const scrollToRef = (ref: any, name: string = "") => {
  if (!ref || !ref.current) {
    console.warn(`No component ${name ? `${name} ` : ""}to scroll to.`);
    return;
  }
  const top =
    getRefTopOffset(ref.current.input ? ref.current.input : ref.current) +
    SCROLL_OFFSET_TOP;
  window.scroll({
    top: top > 0 ? top : 0,
    left: 0,
    behavior: "smooth",
  });
};

const getRefTopOffset = (ref: any) => {
  const parentTop: number = ref.offsetParent
    ? getRefTopOffset(ref.offsetParent)
    : 0;
  return ref.offsetTop + parentTop;
};

export const hasObjectSomeValueInside = (object: any): boolean => {
  if (!object) return false;
  return Object.keys(object).some((key: string) => {
    if (typeof object[key] === "object") {
      return hasObjectSomeValueInside(object[key]);
    }
    if (!object[key]) return false;
    return true;
  });
};

export const removeFromArray = (array: string[], remove: string) =>
  array.filter((item: string) => item !== remove);

export const getObjectPart = (object: any, keys: string[]) => {
  let current = object;
  try {
    keys.forEach((key: string) => {
      current = current[key];
    });
  } catch {
    //object not defined on selected keys, let's just return an empty object
    return {};
  }
  return current;
};

export const removeIndexFromArray = (index: number, array: any[]) => {
  const out = [...array];
  out.splice(index, 1);
  return out;
};

export const getState = (code: string) => {
  return STATES.find((state) => state.value === code);
};

export const stateCodesToOptions = (states: { [key: string]: string }) => {
  if (!states) return [];
  return Object.keys(states).map((code: string) => {
    const state = getState(code);
    return {
      value: code,
      label: state?.label || code,
    } as Option;
  });
};

export const createResponsiveClasses = (
  classes?: ResponsiveClasses,
  prefix?: string
) => {
  if (!classes) return;
  return classNames(
    Object.entries(classes).map((entry: any) => {
      return `${prefix}${entry[0]}-${entry[1]}`;
    })
  );
};

//https://gist.github.com/gordonbrander/2230317
export const generateID = () => "_" + Math.random().toString(36).substr(2, 9);

export const uploadMinDelay = (delay = UPLOAD_MIN_DELAY) =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve(1);
    }, delay);
  });

export const reindexObject = (object: any) => {
  const out: any = {};
  Object.values(object).forEach((value: any, index: number) => {
    out[index] = value;
  });
  return out;
};

export const normalizeValuesForSubmit = (
  values: FormStateValues,
  properties: any
) => {
  const normalized: FormStateValues = {};
  if (typeof values !== "object") return values;
  Object.keys(values).forEach((key: string) => {
    if (values[key] === undefined) return;
    if (!properties[key]) {
      console.warn(`Missing property definition for "${key}".`);
      return;
    }
    if (properties[key].type === "array") {
      normalized[key] = {};
      Object.keys(values[key] as FormStateValues).forEach((key2: string) => {
        (normalized[key] as FormStateValues)[key2] = normalizeValuesForSubmit(
          (values[key] as any)[key2],
          properties[key].properties
        );
      });
    } else {
      normalized[key] = normalizeValueForSubmit(values[key], properties[key]);
    }
  });
  return normalized;
};

const normalizeValueForSubmit = (value: FormStateValue, property: any) => {
  if (value === '' && ['integer', 'float', 'number'].includes(property.type)) {
    return undefined
  }
  if (!value) return value;
  if (property.type === 'string' &&  property.format === 'date-time') {
    if (Array.isArray(value)) {
      const val = value as (Date | undefined)[];
      return val.map((date: Date | undefined) => {
        if (!date) return "";
        return dateToString(date);
      });
    } else {
      return dateToString(value as Date);
    }
  }
  if (property.type === "file") {
    return Object.values(value).map((val: FileI) => val.publicId as string);
  }
  if (property.type === "integer") {
    return parseInt(value.toString().replace(",", "."));
  }
  if (property.type === "float" || property.type === "number") {
    return parseFloat(value.toString().replace(",", "."));
  }

  if (typeof value === "object") {
    return normalizeValuesForSubmit(
      value as FormStateValues,
      property.properties
    );
  }

  return value;
};

export const doesKeyExist = (object: object | undefined, key: string) => {
  if (!object) return false;
  return Object.keys(object).includes(key);
};

export const fillInFileValues = (
  values: any,
  properties: any,
  files: Files
) => {
  const out: FormStateValues = {};
  if (typeof values !== "object" || values === null || properties === undefined)
    return values;
  Object.keys(values).forEach((key: string) => {
    if (values[key] === undefined) return;
    if (properties[key].type === "file") {
      const filesValue: Files = {};
      values[key].forEach((fileID: string) => {
        filesValue[fileID] = files[fileID];
      });
      out[key] = filesValue;
    } else if (properties[key].type === "array") {
      out[key] = {};
      Object.keys(values[key] as FormStateValues).forEach((key2: string) => {
        (out[key] as FormStateValues)[key2] = fillInFileValues(
          (values[key] as any)[key2],
          properties[key].properties,
          files
        );
      });
    } else {
      out[key] = fillInFileValues(
        values[key],
        properties[key].properties,
        files
      );
    }
  });
  return out;
};

export const loadFiles = (fileIDs: string[]) => {
  const promises = fileIDs.map((id: string) =>
    EformApiClient.submissionAttach.getAttachmentPublic(id)
  );
  return Promise.all(promises)
    .then((files: any) => {
      return files.map((file: any) => ({
        id: file.id,
        name: `${file.file.name}.${file.file.extension}`,
        publicId: file.publicId,
        size: file.file.size,
        type: file.file.mimeType,
        nameShort: file.file.name,
        hash: file.file.hash,
        extension: file.file.extension,
      }));
    })
    .then((files: FileI[]) => filesArrayToObject(files));
};

export const filesArrayToObject = (files: FileI[]) => {
  const out: Files = {};
  files.forEach((file: FileI) => {
    out[file.publicId || file.id] = file;
  });
  return out;
};

export const getFileIDsFromData = (values: any, properties: any) => {
  let files: string[] = [];
  if (typeof values !== "object" || values === null || properties === undefined)
    return files;
  Object.keys(values).forEach((key: string) => {
    if (values[key] === undefined) return;
    if (!properties[key]) {
      console.warn(`Missing property definition for "${key}".`);
      return;
    }
    if (properties[key].type === "file") {
      if (Array.isArray(values[key])) {
        files = files.concat(values[key]);
      } else {
        files = files.concat(Object.keys(values[key]));
      }
    } else if (properties[key].type === "array") {
      Object.values(values[key]).forEach((value: any) => {
        files = files.concat(
          getFileIDsFromData(value, properties[key].properties)
        );
      });
    } else {
      files = files.concat(
        getFileIDsFromData(values[key], properties[key].properties)
      );
    }
  });
  return files;
};

export const getFilesData = (values: any, properties: any) => {
  let files: any[] = [];
  if (typeof values !== "object" || values === null || properties === undefined)
    return files;
  Object.keys(values).forEach((key: string) => {
    if (values[key] === undefined) return;
    if (!properties[key]) {
      console.warn(`Missing property definition for "${key}".`);
      return;
    }
    if (properties[key].type === "file") {
      const fileList = Array.isArray(values[key])
        ? values[key]
        : Object.values(values[key]);
      files = files.concat(
        fileList.map((file: FileI) => ({
          id: file.id,
          file: {
            name: file.nameShort,
            extension: file.extension,
            size: file.size,
            hash: file.hash,
          },
        }))
      );
    } else if (properties[key].type === "array") {
      Object.values(values[key]).forEach((value: any) => {
        files = files.concat(getFilesData(value, properties[key].properties));
      });
    } else {
      files = files.concat(
        getFilesData(values[key], properties[key].properties)
      );
    }
  });
  return files;
};

export const getPartOfStateValues = (values: FormStateValues, id: string) => {
  const ids = id.split("/");
  return getObjectPart(values, ids.slice(1, ids.length));
};

export const areUploadsEmpty = (uploads: Uploads) => {
  if (!uploads) return true;
  return !Object.values(uploads).some(
    (fileIDs: string[]) => fileIDs && fileIDs.length !== 0
  );
};

export const userFilesToObject = (files: FileI[]) => {
  const filesOut: Files = {};
  files.forEach((file: FileI) => {
    filesOut[file.name] = {
      id: file.name,
      name: file.name,
      isLoading: true,
      size: file.size,
    };
  });
  return filesOut;
};

export const trimObject = (object: { [key: string]: any }) => {
  const out: any = {};
  Object.keys(object).forEach((key: string) => {
    if (!object[key]) return;
    if (typeof object[key] === "object") {
      const childObject = trimObject(object[key]);
      if (!Object.keys(childObject).length) return;
      out[key] = childObject;
    } else {
      out[key] = object[key];
    }
  });
  return out;
};

export const stringToDate = (value: string) => {
  if (value === "today") return new Date();
  if (value === "tomorrow") {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date;
  }
  if (value === "yesterday") {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date;
  }
  return value ? new Date(value) : undefined;
};

export const dateToString = (value: Date) => toISOStringWithTimezone(value);

const toISOStringWithTimezone = (date: Date) => {
  const tzOffset = -date.getTimezoneOffset();
  const diff = tzOffset >= 0 ? '+' : '-';
  const pad = (n:any) => `${Math.floor(Math.abs(n))}`.padStart(2, '0');
  return date.getFullYear() +
    '-' + pad(date.getMonth() + 1) +
    '-' + pad(date.getDate()) +
    'T' + pad(date.getHours()) +
    ':' + pad(date.getMinutes()) +
    ':' + pad(date.getSeconds()) +
    diff + pad(tzOffset / 60) +
    ':' + pad(tzOffset % 60);
};

export const initValueObjects = (
  values: FormStateValues | undefined,
  properties: any
) => {
  if (!values) return;
  const out: FormStateValues = {};
  Object.keys(values).forEach((key: string) => {
    if (properties[key].type === "object") {
      out[key] = initValueObjects(
        values[key] as FormStateValues,
        properties[key].properties
      );
    } else if (properties[key].type === "array") {
      out[key] = {};
      if (!properties[key].properties) { // simple array hlavne pro ukon
        out[key] = values[key];
        return;
      }
      Object.keys(values[key] as FormStateValues).forEach((key2: string) => {
        (out[key] as FormStateValues)[key2] = initValueObjects(
          (values[key] as any)[key2],
          properties[key].properties
        );
      });
    } else {
      out[key] = initValueObject(values[key], properties[key]);
    }
  });
  return out;
};

export const initValueObject = (value: any, props: any) => {
  if (props.type === 'string' &&  props.format === 'date-time') {
    if (props?.range) {
      return value ? value.map((str: string) => stringToDate(str)) : [];
    }
    return stringToDate(value);
  }
  return value;
};

export const normalizeDynamicFormID = (id: string) => {
  return id.slice(1); //get rid of "/" at the beginning
};

export const randSufix = () => {
  var max = 10000;
  var rand = Math.floor(Math.random() * Math.floor(max));
  return "_____" + rand;
};

export const buildHierarchyID = (
  parentID: string | number | undefined,
  index: string | number | undefined
) => {
  if (parentID === undefined) return index ? index.toString() : "";
  if (index === undefined) return parentID ? parentID.toString() : "";
  return `${parentID}___${index}`;
};

// https://gist.github.com/mathewbyrne/1280286
export const slugify = (str: string) =>
  str
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");

// https://stackoverflow.com/questions/610406/javascript-equivalent-to-printf-string-format/4673436#4673436
export const formatString = (str?: string, ...args: any[]) => {
  if (!str) return str;
  return str.replace(/{(\d+)}/g, function (match, number) {
    return typeof args[number] != "undefined" ? args[number] : match;
  });
};
